import { useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { getProperties } from "../api/properties";
import { createExpense } from "../api/expenses";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

const initialState = {
  type: "",
  description: "",
  propertyId: "",
  amount: "",
  date: "",
  recurring: false,
  recurrenceInterval: "",
};

const expenseTypes = [
  { value: "", label: "Select expense type" },
  { value: "maintenance", label: "Maintenance" },
  { value: "mortgage", label: "Mortgage" },
  { value: "insurance", label: "Insurance" },
  { value: "utilities", label: "Utilities" },
  { value: "tax", label: "Tax" },
  { value: "repairs", label: "Repairs" },
  { value: "management", label: "Management Fees" },
  { value: "other", label: "Other" },
];

const recurrenceOptions = [
  { value: "", label: "Select interval" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

export default function AddExpenseDrawer({ onSave, onClose }) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const { run: runSubmission, isPending } = useSubmissionLock();

  useEffect(() => {
    getProperties()
      .then((res) => {
        setProperties(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load properties:", err);
      })
      .finally(() => {
        setLoadingProperties(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const isFormValid =
    form.type.trim() && form.propertyId.trim() && form.amount && form.date;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending("expense:create")) return;
    if (!isFormValid) return;

    await runSubmission("expense:create", async () => {
      setSaving(true);
      try {
        const payload = {
          type: form.type,
          description: form.description,
          propertyId: form.propertyId,
          amount: parseFloat(form.amount) || 0,
          date: form.date,
        };

        const res = await createExpense(payload);
        const created = res?.data ?? payload;

        onSave?.(created);
        alert("Expense added");
        setForm(initialState);
        onClose?.();
      } catch (err) {
        console.error("Error adding expense:", err);
        alert("Failed to add expense");
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative ml-auto h-full w-full max-w-xl overflow-y-auto bg-[linear-gradient(to_bottom,_#2A2A2A,_#1A1A1A)] p-8 text-white">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold mb-6">Add Expense</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <label className="block mb-1 font-semibold">Expense Type</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              required
              className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
            >
              {expenseTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
          </div>

          <div className="relative">
            <label className="block mb-1 font-semibold">Property</label>
            <select
              name="propertyId"
              value={form.propertyId}
              onChange={handleChange}
              disabled={loadingProperties}
              required
              className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
            >
              <option value="">
                {loadingProperties ? "Loading properties..." : "Select property"}
              </option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || p.name || "Untitled Property"}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="e.g. Plumbing repair, council tax, etc."
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block mb-1 font-semibold">Amount (£)</label>
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
                placeholder="e.g. 250.00"
              />
            </div>

            <div className="flex-1">
              <label className="block mb-1 font-semibold">Date</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 font-semibold">Recurring Payment</label>
            <div className="flex items-center gap-3 mb-2">
              <input
                type="checkbox"
                name="recurring"
                checked={form.recurring}
                onChange={handleChange}
                className="w-5 h-5 accent-[#AF9B8A]"
              />
              <span>Mark as recurring expense</span>
            </div>

            {form.recurring && (
              <div className="relative">
                <label className="block mb-1 text-sm text-gray-300">
                  Recurrence Interval
                </label>
                <select
                  name="recurrenceInterval"
                  value={form.recurrenceInterval}
                  onChange={handleChange}
                  className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
                >
                  {recurrenceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={!isFormValid || saving}
            className="w-full py-3 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Adding expense..." : "Add Expense"}
          </button>
        </form>
      </div>
    </div>
  );
}
